import { CommonModule } from "@angular/common";
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgGlyph } from "@ng-icons/core";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { AppSummaryPanelComponent, SummaryPanelRow } from "../../../../Common/Components/SummaryPanel/SummaryPanelComponent";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import {
  TableColumn,
  TableConfig,
  TablePagination,
  TableSearchEvent,
  TableSort,
} from "../../../../Common/Components/Table/Type/TableType";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { StockMovement } from "../../Dto/StockMovementModel";
import { StockDetailEffects } from "../../State/StockDetailEffects";
import { StockDetailState } from "../../State/StockDetailState";

@Component({
  selector: "app-stock-detail",
  standalone: true,
  imports: [CommonModule, NgGlyph, AppButtonComponent, AppCardComponent, AppSummaryPanelComponent, AppTableComponent],
  templateUrl: "./StockDetailPage.html",
  styleUrl: "./StockDetailPage.scss",
  providers: [StockDetailState, StockDetailEffects],
  encapsulation: ViewEncapsulation.None,
})
export class StockDetailPage implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly stockDetailEffects = inject(StockDetailEffects);
  readonly stockDetailState = inject(StockDetailState);

  readonly pageSize = 20;
  private stockId = "";
  readonly movementColumns: TableColumn<StockMovement>[] = [
    {
      key: "createdAt",
      header: "Date",
      width: "16%",
      cellTemplate: (item) => this.formatDate(item.createdAt),
    },
    {
      key: "sourceType",
      header: "Type",
      width: "14%",
      cellTemplate: (item) => item.sourceType,
      cellClass: (item) => this.getTypeClass(item),
      cellIcon: (item) => this.getTypeIcon(item),
    },
    {
      key: "qtyIn",
      header: "Qty In",
      width: "10%",
      cellTemplate: (item) => this.formatQuantity(item.qtyIn),
      cellClass: (item) => this.getDeltaClass(item.qtyIn),
    },
    {
      key: "qtyOut",
      header: "Qty Out",
      width: "10%",
      cellTemplate: (item) => this.formatQuantity(item.qtyOut),
      cellClass: (item) => this.getDeltaClass(-item.qtyOut),
    },
    {
      key: "reservedDelta",
      header: "Reserved",
      width: "10%",
      cellTemplate: (item) => this.formatQuantity(item.reservedDelta),
      cellClass: (item) => this.getDeltaClass(item.reservedDelta),
    },
    {
      key: "sourceId",
      header: "Source",
      width: "16%",
      cellTemplate: (item) => this.getSourceLabel(item),
    },
    {
      key: "note",
      header: "Note",
      width: "14%",
      cellTemplate: (item) => this.formatNote(item.note),
    },
  ];

  async ngOnInit(): Promise<void> {
    this.stockId = this.route.snapshot.paramMap.get("id") ?? "";

    if (!this.stockId) {
      this.stockDetailState.setStockError("Missing stock record id.");
      this.stockDetailState.setMovementError("Missing stock record id.");
      return;
    }

    await Promise.all([
      this.stockDetailEffects.loadStock(this.stockId),
      this.stockDetailEffects.loadMovements(this.stockId, 1, this.pageSize),
    ]);
  }

  ngOnDestroy(): void {
    this.stockDetailState.reset();
  }

  goBack(): void {
    this.router.navigate(["/stocks"]).catch(() => undefined);
  }

  get movementConfig(): TableConfig<StockMovement> {
    return {
      loading: this.stockDetailState.loadingMovements(),
      emptyMessage: this.stockDetailState.movementError() || "No stock movements have been recorded yet.",
      showActions: true,
      actionButtons: [
        {
          label: "Open Source",
          icon: "open_in_new",
          visible: (item) => this.canOpenSource(item),
          handler: (item) => {
            void this.openSource(item);
          },
        },
      ],
    };
  }

  get movementPagination(): TablePagination {
    return this.stockDetailState.pagination();
  }

  get summaryRows(): SummaryPanelRow[] {
    return [
      { label: "On Hand", value: this.formatMetricQuantity(this.stockDetailState.stock()?.onHand ?? 0) },
      { label: "Reserved", value: this.formatMetricQuantity(this.stockDetailState.stock()?.reserved ?? 0) },
      { label: "Available", value: this.formatMetricQuantity(this.stockDetailState.stock()?.available ?? 0) },
    ];
  }

  onPageChange(event: { page: number; search?: string }): void {
    if (!this.stockId) {
      return;
    }

    void this.stockDetailEffects.loadMovements(this.stockId, event.page, this.stockDetailState.pagination().limit || this.pageSize);
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    if (!this.stockId) {
      return;
    }

    void this.stockDetailEffects.loadMovements(this.stockId, 1, event.limit);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSearchChange(event: TableSearchEvent): void {
    return;
  }

  getTypeClass(movement: StockMovement): string {
    if (movement.sourceType === "Adjustment") {
      return "app-table__chip app-table__chip--adjustment";
    }

    if (movement.sourceType === "Printing") {
      return "app-table__chip app-table__chip--printing";
    }

    return "app-table__chip app-table__chip--request";
  }

  getTypeIcon(movement: StockMovement): string {
    if (movement.sourceType === "Adjustment") {
      return "tune";
    }

    if (movement.sourceType === "Printing") {
      return "print";
    }

    return "move_to_inbox";
  }

  formatQuantity(value: number): string {
    return new Intl.NumberFormat("en-US", { signDisplay: "exceptZero" }).format(value);
  }

  formatMetricQuantity(value: number): string {
    return new Intl.NumberFormat("en-US").format(value);
  }

  formatDate(value?: string): string {
    return (() => {
      if (value) {
        return formatDateNumeric(value);
      }
      return "-";
    })();
  }

  formatNote(value?: string | null): string {
    if (!value?.trim()) {
      return "-";
    }

    if (typeof document === "undefined") {
      return (
        value
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .trim() || "-"
      );
    }

    const container = document.createElement("div");
    container.innerHTML = value;
    const normalized = container.textContent?.replace(/\s+/g, " ").trim();
    return normalized || "-";
  }

  getSourceLabel(movement: StockMovement): string {
    return `${movement.sourceType} · ${movement.sourceId}`;
  }

  getDeltaClass(value: number): string {
    if (value > 0) {
      return "stock-movement-table__delta stock-movement-table__delta--positive";
    }

    if (value < 0) {
      return "stock-movement-table__delta stock-movement-table__delta--negative";
    }

    return "stock-movement-table__delta";
  }

  canOpenSource(movement: StockMovement): boolean {
    return movement.sourceType === "Request" || movement.sourceType === "Printing";
  }

  async openSource(movement: StockMovement): Promise<void> {
    const route = (() => {
      if (movement.sourceType === "Request") {
        return ["/requests/edit", movement.sourceId];
      }
      return (() => {
        if (movement.sourceType === "Printing") {
          return ["/printings/edit", movement.sourceId];
        }
        return null;
      })();
    })();

    if (!route) {
      return;
    }

    await this.router.navigate(route);
  }
}
